import { useSession } from '../../session'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRightCircle, ChevronLeft, ChevronRight, Download, Plus, Search, Send } from 'lucide-react'
import { convertCustomerQuote, listCustomerQuotes, sendCustomerQuote } from '../../api/sales'
import { useToast } from '../../components/Toast.jsx'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, parseWallClock, startOfToday } from '../../utils/format'
import SalesTabs from './SalesTabs'
import { money, num, statusOf } from './salesShared'

const PAGE_SIZE = 15

const customerText = (row) => row?.customer_name || (row?.customer_id ? `Customer #${row.customer_id}` : 'No customer')
const quoteNo = (row) => row.quote_number || 'Draft'

/** Draft, sent (waiting on the customer), expired, converted or void. */
function stateOf(row) {
  const s = statusOf(row)
  if (s === 'void' || s === 'voided') return 'void'
  if (s === 'converted') return 'converted'
  if (s === 'sent') {
    const until = parseWallClock(row.valid_until)
    return until && until < startOfToday() ? 'expired' : 'sent'
  }
  return 'draft'
}

const STATE = {
  draft: { label: 'Draft', tone: 'muted' },
  sent: { label: 'Waiting on customer', tone: 'info' },
  expired: { label: 'Expired', tone: 'warn' },
  converted: { label: 'Invoiced', tone: 'good' },
  void: { label: 'Void', tone: 'bad' },
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Drafts' },
  { id: 'sent', label: 'Waiting on customer' },
  { id: 'expired', label: 'Expired' },
  { id: 'converted', label: 'Invoiced' },
  { id: 'void', label: 'Void' },
]

const cleanCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`

export default function CustomerQuoteList() {
  const { has } = useSession()
  const nav = useNavigate()
  const { notify } = useToast()
  const [rows, setRows] = useState([])
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState(null)

  const refresh = async () => {
    setLoading(true)
    setError('')
    try {
      setRows(await listCustomerQuotes())
    } catch (e) {
      setRows([])
      setError(e.message || 'Could not load quotes.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, rows.filter((r) => f.id === 'all' || stateOf(r) === f.id).length])
  ), [rows])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    const out = rows.filter((row) => (filter === 'all' || stateOf(row) === filter) && (!term || [
      row.quote_number, row.quote_id, customerText(row), row.memo, row.converted_invoice_number,
    ].join(' ').toLowerCase().includes(term)))
    const key = (row) => {
      if (sortKey === 'customer') return customerText(row).toLowerCase()
      if (sortKey === 'date') return parseWallClock(row.quote_date)?.getTime() || 0
      if (sortKey === 'valid') return parseWallClock(row.valid_until)?.getTime() || 0
      if (sortKey === 'total') return num(row.total_amount)
      return String(row.quote_number || row.quote_id || '')
    }
    out.sort((a, b) => {
      const av = key(a)
      const bv = key(b)
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
    return out
  }, [rows, search, sortKey, sortDir, filter])

  useEffect(() => { setPage(1) }, [search, filter, sortKey, sortDir])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageSafe = Math.min(page, pageCount)
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE)

  const setSort = (key) => {
    if (sortKey === key) setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(['total', 'date'].includes(key) ? 'desc' : 'asc')
    }
  }
  const sortHead = (key, label, className = '') => (
    <th className={`sortable ${className}`.trim()} onClick={() => setSort(key)} aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
      {label} <span>{sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : ''}</span>
    </th>
  )

  const send = async (quoteId) => {
    setBusyId(quoteId)
    try {
      await sendCustomerQuote(quoteId)
      notify('Quote marked as sent')
      await refresh()
    } catch (e) {
      notify(e.message || 'Could not send the quote.')
    } finally {
      setBusyId(null)
    }
  }

  const convert = async (quoteId) => {
    setBusyId(quoteId)
    try {
      const result = await convertCustomerQuote(quoteId)
      notify(result.invoice_number ? `Draft invoice ${result.invoice_number} created` : 'Draft invoice created')
      nav(`/sales/customer-invoices/${result.invoice_id}`)
    } catch (e) {
      notify(e.message || 'Could not turn the quote into an invoice.')
      setBusyId(null)
    }
  }

  const exportCsv = () => {
    const header = ['Quote', 'Customer', 'Date', 'Valid until', 'Status', 'Total']
    const lines = filtered.map((row) => [
      quoteNo(row),
      customerText(row),
      fmtDay(row.quote_date),
      fmtDay(row.valid_until),
      STATE[stateOf(row)].label,
      num(row.total_amount).toFixed(2),
    ].map(cleanCsv).join(','))
    const blob = new Blob([[header.map(cleanCsv).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'quotes.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <SalesTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Quotes</h1>
            <div className="ci-title-meta">Prices you offer a customer. When they accept, turn the quote into an invoice.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          {has('invoices') ? <Link className="ci-btn ci-btn-primary" to="/sales/quotes/new"><Plus size={15} />New quote</Link> : <Link className="ci-btn ci-btn-primary" to="/jobs"><Plus size={15} />Start from a job</Link>}
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search quotes">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Quote number or customer…" />
        </label>
        <div className="ci-toolbar-right">
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={exportCsv} disabled={!filtered.length}>
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      <FilterChips options={FILTERS.map((f) => ({ id: f.id, label: f.label, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      {error && <ErrorBanner message={error} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                {sortHead('num', 'Quote')}
                {sortHead('customer', 'Customer')}
                {sortHead('date', 'Date')}
                {sortHead('valid', 'Valid until')}
                <th className="center">Status</th>
                {sortHead('total', 'Total', 'num')}
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><Spinner label="Loading quotes…" /></td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">{rows.length ? 'No quotes match.' : 'No quotes yet.'}</div></td></tr>
              ) : pageRows.map((row) => {
                const st = stateOf(row)
                return (
                  <tr key={row.quote_id} className="row-link" onClick={() => nav(`/sales/quotes/${row.quote_id}`)}>
                    <td><Link className="ci-je-link" to={`/sales/quotes/${row.quote_id}`} onClick={(e) => e.stopPropagation()}>{quoteNo(row)}</Link></td>
                    <td className="ci-strong">{customerText(row)}</td>
                    <td className="ci-muted-cell">{fmtDay(row.quote_date)}</td>
                    <td className={st === 'expired' ? 'ci-overdue-cell' : 'ci-muted-cell'}>{fmtDay(row.valid_until) || '–'}</td>
                    <td className="center"><StatusBadge label={STATE[st].label} tone={STATE[st].tone} /></td>
                    <td className="num ci-total">{money(row.total_amount)}</td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      {st === 'draft' && (
                        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => send(row.quote_id)} disabled={busyId === row.quote_id}>
                          <Send size={13} />
                          {busyId === row.quote_id ? 'Sending…' : 'Mark as sent'}
                        </button>
                      )}
                      {(st === 'sent' || st === 'expired') && (
                        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => convert(row.quote_id)} disabled={busyId === row.quote_id}>
                          <ArrowRightCircle size={13} />
                          {busyId === row.quote_id ? 'Working…' : 'Make invoice'}
                        </button>
                      )}
                      {st === 'converted' && row.converted_invoice_id && (
                        <Link className="ci-je-link" to={`/sales/customer-invoices/${row.converted_invoice_id}`}>{row.converted_invoice_number || 'View invoice'}</Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{filtered.length} {filtered.length === 1 ? 'quote' : 'quotes'}</span>
          {pageCount > 1 && (
            <div className="ci-page-buttons">
              <button type="button" className="ci-page-btn" disabled={pageSafe === 1} onClick={() => setPage(pageSafe - 1)}><ChevronLeft size={13} />Previous</button>
              <span className="ci-page-info">Page {pageSafe} of {pageCount}</span>
              <button type="button" className="ci-page-btn" disabled={pageSafe === pageCount} onClick={() => setPage(pageSafe + 1)}>Next<ChevronRight size={13} /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
